<template lang='pug'>
  .early-access-cta
    template(v-if='!!currentUser')
      button.btn.btn-block.btn-primary.product-card-cta(
        :class='btnClasses'
        @click='subscribeNotification'
        v-text='buttonText'
        :disabled='btnDisabled'
      )
    template(v-else)
      router-link.btn.btn-block.btn-primary.product-card-cta(
        :to='{ query: { ...$router.currentRoute.query, notify: true }, hash: "authentication" }' append replace
        :class='btnClasses'
        @click.native='recordRemindMeGtmEvent'
        v-text='$t("early_access.buttons.remindMe")'
      )
</template>

<script>
import { mapState } from 'vuex'
import { variantUtil } from '@/mixins/variantUtil'
import User from '@/modules/api/user'
import { pushDataLayerEvent } from '@/modules/services/gtm'

const userApi = new User()

export default {
  mixins: [
    variantUtil
  ],

  props: {
    variant: {
      type: Object,
      default: () => {}
    },
    product: {
      type: Object,
      default: () => {}
    },
    btnClasses: {
      type: String,
      default: ''
    },
    isSmallButton: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isSubscribed: false,
      currentBtnText: '',
      isBtnDisabled: false
    }
  },

  computed: {
    ...mapState({
      earlyAccessCtaText: state => state.product.earlyAccessCtaText,
      earlyAccessCtaStickyText: state => state.product.earlyAccessCtaStickyText,
      earlyAccessCtaDisabled: state => state.product.earlyAccessCtaDisabled,
      notification: state => state.layout.notification
    }),

    isWishlistPage () {
      return this.$route.name === 'wishlist'
    },

    buttonText () {
      if (this.isWishlistPage) {
        return this.currentBtnText
      } else {
        return this.isSmallButton && !this.isPreviewPhase && !this.isSupportedPlatform ? this.earlyAccessCtaStickyText : this.earlyAccessCtaText
      }
    },

    btnDisabled () {
      return this.isWishlistPage ? this.isBtnDisabled : this.earlyAccessCtaDisabled
    }
  },

  watch: {
    variant () {
      this.updateEarlyAccessCtaText()
    },

    async currentUser () {
      await this.updateEarlyAccessCtaText()
      const { notify, ...query } = this.$route.query
      if (notify) {
        this.$router.replace({ query })
        if (!this.isSubscribed && this.isPreviewPhase && !this.earlyAccessCtaDisabled) this.subscribeNotification()
      }
    }
  },

  mounted () {
    this.$nextTick(async () => {
      await this.updateEarlyAccessCtaText()
    })
  },

  methods: {
    async subscribeNotification () {
      try {
        this.recordRemindMeGtmEvent()
        this.$store.commit('setEarlyAccessCtaDisabled', true)
        const params = this.notificationParams()
        await userApi.subscribeVariant(params)
        this.$store.commit('setEarlyAccessCtaText', this.$t('early_access.buttons.remindMe'))
        this.isSubscribed = true
        if (this.isWishlistPage) this.isBtnDisabled = true
        this.updateNotification('success', this.$t('early_access.successfullyNotified'))
      } catch (error) {
        this.$store.commit('setEarlyAccessCtaDisabled', false)
        this.isSubscribed = false
        this.isBtnDisabled = false
        this.updateNotification('error', this.$t('notifications.error'))
      }
    },

    async updateEarlyAccessCtaText () {
      if (this.isEarlyAccessEnabled && this.currentUser) {
        let text = ''
        let isDisabled = false

        if (this.isPreviewPhase) {
          await this.notificationCheck()
          isDisabled = this.isSubscribed
          text = this.$t('early_access.buttons.remindMe')
        } else if (!this.isSupportedPlatform) {
          isDisabled = true

          if (this.isSmallButton) {
            text = this.$t('buttons.appExclusive')
            this.$store.commit('setEarlyAccessCtaStickyText', text)
          }

          if (!this.isWishlistPage) text = this.$t('early_access.buttons.appExclusive')
        }

        this.currentBtnText = text
        this.isBtnDisabled = isDisabled
        this.$store.commit('setEarlyAccessCtaText', text)
        this.$store.commit('setEarlyAccessCtaDisabled', isDisabled)
      }
    },

    async notificationCheck () {
      try {
        const data = await userApi.fetchSubscriptions(this.currentUser?.id)
        this.isSubscribed = data.includes(parseInt(this.variant.id))
      } catch (error) {
        this.updateNotification('error', this.$t('notifications.error'))
      }
    },

    updateNotification (type, msg) {
      this.$store.commit('setNotification', {
        type: type,
        msg: msg
      })
    },

    notificationParams () {
      const type = this.isQualifiedUser ? 'prelaunch' : 'launch'
      const params = {
        event_type: `products_${type}`,
        event_name: this.launchConfig[type]?.name,
        subscribable_type: 'Variant',
        subscribable_id: this.variant.id
      }

      return { notificationParams: params, userId: this.currentUser?.id }
    },

    recordRemindMeGtmEvent () {
      pushDataLayerEvent(
        'ea_remind_me', {
          params: {
            ...this.trackingParams,
            product_details: this.variant.attributes['tags'].map(tag => tag['name']).join(' | ')
          }
        }
      )
    }
  }
}
</script>

<style lang='scss' scoped>
  .early-access-cta {
    width: 100%;
    .btn-primary {
      background-color: $black;
      border-radius: 5px;
      border-color: $black;
    }
    .btn-primary:disabled {
      color: #ccc;
      background-color: #f1f1f1;
      border-color: #f1f1f1;
      cursor: not-allowed;
      opacity: 1;
    }
  }
</style>
